import { Drawer, Form, message } from 'antd';
import { useContext } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import type { MaxBookingConfigurationInput } from 'app/hooks/data/core/extranet/pack/mapping/pack';
import { mapToMaxBookingConfiguration } from 'app/hooks/data/core/extranet/pack/mapping/pack';
import { useSavePackConfiguration } from 'app/hooks/data/core/extranet/pack/useSavePack';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';

import { inventoryStore } from '../../../../InventoryStore';

import styles from './MaxBookingDrawer.module.scss';
import { MaxBookingContent } from './_components/MaxBookingContent';
import { MaxBookingFooter } from './_components/MaxBookingFooter';

export const MaxBookingDrawer = () => {
  const [form] = Form.useForm();
  const { hotelId } = useContext(HotelContext);

  const { mutateAsync: savePackConfiguration } = useSavePackConfiguration();

  const { drawerPackConfig, setDrawerPackConfig } = useStore(
    inventoryStore,
    useShallow((state) => ({
      drawerPackConfig: state.drawerPackConfig,
      setDrawerPackConfig: state.setDrawerPackConfig,
    }))
  );

  const { getPack } = useContext(ModernHotelContext);

  const pack = drawerPackConfig?.packId
    ? getPack(drawerPackConfig.packId)
    : null;

  const getInitialValues = () => {
    if (!pack) {
      return { isToggled: false };
    }

    const config = pack.maxBookingConfiguration;

    if (!config) {
      return { isToggled: false };
    }

    if (config.type === OPENING_CONFIGURATION_TYPE.DAILY) {
      const daysWithIsToggled = Object.entries(config.days).reduce(
        (acc, [day, dayConfig]) => ({
          ...acc,
          [day]: {
            ...dayConfig,
            isToggled: (dayConfig.maxBooking ?? 0) > 0,
          },
        }),
        {}
      );

      return {
        type: config.type,
        days: daysWithIsToggled,
        isToggled: true,
      };
    }

    return {
      ...config,
      isToggled: !!config.configuration,
    };
  };

  const handleClose = () => {
    setDrawerPackConfig(undefined, false);
  };

  const handleDrawerOpenChange = (isOpen: boolean) => {
    if (isOpen && drawerPackConfig?.packId) {
      form.resetFields();
    }
  };

  const handleOnFinish = async (values: MaxBookingConfigurationInput) => {
    if (!pack) {
      return;
    }

    await savePackConfiguration({
      hotelId,
      packId: pack?.id,
      openingConfiguration: mapToMaxBookingConfiguration(values),
    })
      .then(() => {
        message.success('Max booking configuration saved successfully');
        handleClose();
      })
      .catch(() => message.error('Failed to save max booking configuration'));
  };

  return (
    <Drawer
      title={
        <div className={styles.maxBookingHeader}>{pack?.nameForHotel}</div>
      }
      className={styles.maxBookingDrawerConfig}
      width="510px"
      closable
      open={!!drawerPackConfig?.isOpen}
      bodyStyle={{ padding: 0 }}
      onClose={handleClose}
      afterOpenChange={handleDrawerOpenChange}
      destroyOnClose
      footer={
        <MaxBookingFooter handleClose={handleClose} handleSave={form.submit} />
      }
    >
      <Form
        key={drawerPackConfig?.packId}
        name={`maxBooking-drawer-${drawerPackConfig?.packId}`}
        form={form}
        onFinish={handleOnFinish}
        initialValues={getInitialValues()}
        preserve={false}
      >
        <MaxBookingContent />
      </Form>
    </Drawer>
  );
};
